import { isAffirmative } from "@/utils/isAffirmative/isAffirmative";

export type EapContactNumber = {
  region: string;
  number: string;
  href: string;
};

export type EapContactChannels = {
  callNumbers: EapContactNumber[];
  whatsappNumbers: EapContactNumber[];
  smsNumbers: EapContactNumber[];
  hasLiveChat: boolean;
};

type EapContactDefaults = {
  eap_phone?: unknown;
  live_chat?: unknown;
};

const WHATSAPP_LABEL = /whatsapp/i;
const SMS_LABEL = /\bsms\b/i;

// wa.me needs the full international number. Core only rewrites numbers labelled exactly
// "UK" or "IE" into international format, so "WhatsApp IE" arrives in national form.
const COUNTRY_CODE_BY_REGION: Record<string, string> = {
  UK: "44",
  IE: "353",
};

// Core has no field for how a number is used, so the label carries it: "UK" is a call
// line, "SMS IE" a text line, "SMS & WhatsApp UK" both. What's left once the channel
// words are removed is the region shown to the member.
const regionFromLabel = (label: string): string =>
  label
    .replace(/whatsapp|\bsms\b/gi, "")
    .replace(/[&/,+]|\band\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

// The number field is free text in Core ("Text 'Hi' to +44 74 1836 0046"), so links are
// built from the first number-shaped run rather than from every digit in the field.
const PHONE_NUMBER_RUN = /\+?\d[\d\s().-]{5,}\d/;

// "Text 'Hi' to …" — WhatsApp's click-to-chat link can carry that opening message.
const SUGGESTED_MESSAGE = /\btext\s+['‘"“]([^'‘’"“”]+)['’"”]/i;

const dialableNumber = (value: string): string =>
  (value.match(PHONE_NUMBER_RUN)?.[0] ?? "").replace(/[^\d+]/g, "");

const toWhatsappNumber = (dialable: string, region: string): string => {
  const digits = dialable.replace(/^\+/, "");
  if (digits.startsWith("00")) return digits.slice(2);
  const countryCode = COUNTRY_CODE_BY_REGION[region.toUpperCase()];
  if (countryCode && /^0[1-9]/.test(digits))
    return countryCode + digits.slice(1);
  return digits;
};

const whatsappHref = (
  number: string,
  dialable: string,
  region: string,
): string => {
  const suggestedMessage = number.match(SUGGESTED_MESSAGE)?.[1].trim();
  const chatLink = `https://wa.me/${toWhatsappNumber(dialable, region)}`;
  return suggestedMessage
    ? `${chatLink}?text=${encodeURIComponent(suggestedMessage)}`
    : chatLink;
};

export const extractEapContactChannels = (
  defaults: EapContactDefaults | null | undefined,
): EapContactChannels => {
  const channels: EapContactChannels = {
    callNumbers: [],
    whatsappNumbers: [],
    smsNumbers: [],
    hasLiveChat: isAffirmative(defaults?.live_chat),
  };

  const phones = defaults?.eap_phone;
  if (!Array.isArray(phones)) return channels;

  phones.forEach((entry) => {
    if (!Array.isArray(entry)) return;
    const [label, encodedNumber] = entry;
    if (typeof label !== "string" || typeof encodedNumber !== "string") return;
    const number = decodeURIComponent(encodedNumber).trim();
    const dialable = dialableNumber(number);
    if (!dialable) return;

    const region = regionFromLabel(label);
    const isWhatsapp = WHATSAPP_LABEL.test(label);
    const isSms = SMS_LABEL.test(label);

    if (isWhatsapp) {
      channels.whatsappNumbers.push({
        region,
        number,
        href: whatsappHref(number, dialable, region),
      });
    }
    if (isSms) {
      channels.smsNumbers.push({
        region,
        number,
        href: `sms:${dialable}`,
      });
    }
    if (!isWhatsapp && !isSms) {
      channels.callNumbers.push({
        region,
        number,
        href: `tel:${dialable}`,
      });
    }
  });

  return channels;
};

export const hasMessagingChannel = ({
  hasLiveChat,
  whatsappNumbers,
  smsNumbers,
}: EapContactChannels): boolean =>
  hasLiveChat || whatsappNumbers.length > 0 || smsNumbers.length > 0;
